import { create } from 'zustand';

/**
 * usePendingLayoutStore — tracks canvas changes since the last layout run.
 *
 * The rule (v5.3): ELK auto-runs on node ADDITIONS (we need positions for
 * new nodes) and on initial load. It DOES NOT auto-run on node REMOVALS
 * (stash, filter, remove) — existing nodes keep their positions, and the
 * user gets a visual nudge on the floating bar's Recompute button to
 * compact the layout if they want.
 *
 * GraphCanvas's elements-watch effect owns the counter updates: it bumps
 * `removedSinceLayout` when it detects a removal-only change and skips
 * the layout. When a layout actually runs (addition, manual rearrange,
 * initial load), it calls `clear()`.
 *
 * Consumers:
 *   - Workbench floating bar: highlights "Recompute" with a count badge
 *     when `removedSinceLayout > 0`
 *   - GraphCanvas: writes
 */
interface PendingLayoutState {
    removedSinceLayout: number;
    addedSinceLayout: number;
    bumpRemoved: (n: number) => void;
    bumpAdded: (n: number) => void;
    clear: () => void;
    /**
     * Streaming hold (R24 stability rule). While true, GraphCanvas defers every
     * expansion layout: SSE `node_discovered` / `edge_discovered` flushes add
     * elements but do not relayout. Layout runs when `releaseLayout()` is called
     * (step_result, complete, user action) or when the hold is cleared.
     */
    streamHold: boolean;
    /** Bumped by `releaseLayout()`; GraphCanvas re-enters its layout effect on change. */
    releaseTick: number;
    /** True from a `releaseLayout()` until GraphCanvas runs (or finds nothing to run) that one layout. */
    releaseGranted: boolean;
    consumeRelease: () => void;
    beginStreamHold: () => void;
    /** Release one layout now (a step boundary) and keep holding for the next step. */
    releaseLayout: () => void;
    /** Stream over (complete, error, cancel, unmount): drop the hold and run the pending layout. */
    endStreamHold: () => void;
}

export const usePendingLayoutStore = create<PendingLayoutState>((set) => ({
    removedSinceLayout: 0,
    addedSinceLayout: 0,
    bumpRemoved: (n: number) => set((s) => ({ removedSinceLayout: s.removedSinceLayout + n })),
    bumpAdded: (n: number) => set((s) => ({ addedSinceLayout: s.addedSinceLayout + n })),
    clear: () => set({ removedSinceLayout: 0, addedSinceLayout: 0 }),
    streamHold: false,
    releaseTick: 0,
    releaseGranted: false,
    consumeRelease: () => set({ releaseGranted: false }),
    beginStreamHold: () => set({ streamHold: true, releaseGranted: false }),
    releaseLayout: () => set((s) => ({ releaseTick: s.releaseTick + 1, releaseGranted: true })),
    endStreamHold: () => set((s) => ({ streamHold: false, releaseGranted: false, releaseTick: s.releaseTick + 1 })),
}));
